import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Search, Trash2 } from "lucide-react";
import { Logo, Wordmark } from "../components/Logo";
import { API, apiClient } from "../lib/api";

const STATUSES = ["new", "contacted", "closed"];
const statusStyle = {
  new: "bg-brand/15 text-brand border-brand/30",
  contacted: "bg-amber-400/10 text-amber-300 border-amber-300/30",
  closed: "bg-zinc-500/10 text-[#C9D2DE] border-zinc-500/30",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [emailSearch, setEmailSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (typeFilter !== "all") params.type = typeFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (emailSearch.trim()) params.email = emailSearch.trim();
      const [enquiries, statsRes] = await Promise.all([
        apiClient.get("/admin/enquiries", { params }),
        apiClient.get("/admin/stats"),
      ]);
      setItems(enquiries.data.items);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error("Could not load enquiries");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, emailSearch, navigate]);

  useEffect(() => {
    apiClient
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const setStatus = async (id, status) => {
    try {
      await apiClient.patch(`/admin/enquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteEnquiry = async (id, name) => {
    if (!window.confirm(`Permanently delete the enquiry from ${name}? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/admin/enquiries/${id}`);
      toast.success("Enquiry permanently deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = async () => {
    await apiClient.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const filterBtn = (active) =>
    `rounded-full px-4 py-2 text-center font-display text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-200 ${
      active ? "bg-brand text-black" : "border border-white/15 text-[#C9D2DE] hover:text-white"
    }`;

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="flex h-16 items-center justify-between border-b border-white/10 px-5 md:px-8">
        <div className="flex items-center gap-3">
          <Logo className="h-10 w-10" />
          <Wordmark />
          <span className="ml-3 border border-white/15 px-2 py-0.5 font-display text-[10px] font-bold uppercase tracking-[0.25em] text-[#A8B4C2]">
            Enquiries
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden text-xs text-[#A8B4C2] md:block" data-testid="admin-user-email">{user?.email}</span>
          <button onClick={load} className="text-[#C9D2DE] transition-colors hover:text-brand" aria-label="Refresh" data-testid="admin-refresh-button">
            <RefreshCw size={16} />
          </button>
          <button onClick={logout} className="flex items-center gap-1.5 font-display text-xs font-bold uppercase tracking-[0.15em] text-[#C9D2DE] transition-colors hover:text-white" data-testid="admin-logout-button">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 md:px-8">
        {stats && (
          <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-3 lg:grid-cols-5" data-testid="admin-stats">
            {[
              ["New messages", stats.contact.new],
              ["New bookings", stats.booking.new],
              ["New health checks", stats["health-check"].new],
              ["In progress", stats.contact.contacted + stats.booking.contacted + stats["health-check"].contacted],
              ["Closed", stats.contact.closed + stats.booking.closed + stats["health-check"].closed],
            ].map(([label, value]) => (
              <div key={label} className="bg-ink p-6">
                <p className="font-display text-3xl font-black text-white">{value}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-[#A8B4C2]">{label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="admin-filters">
          {["all", "contact", "booking", "health-check"].map((t) => (
            <button key={t} onClick={() => setTypeFilter(t)} className={filterBtn(typeFilter === t)} data-testid={`filter-type-${t}`}>
              {t === "all" ? "All" : t === "contact" ? "Messages" : t === "booking" ? "Bookings" : "Health checks"}
            </button>
          ))}
          <span className="mx-2 h-6 w-px bg-white/10" />
          {["all", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} className={filterBtn(statusFilter === s)} data-testid={`filter-status-${s}`}>
              {s}
            </button>
          ))}
          <span className="mx-2 h-6 w-px bg-white/10" />
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8B4C2]" />
            <input
              type="email"
              value={emailSearch}
              onChange={(e) => setEmailSearch(e.target.value)}
              placeholder="Privacy lookup by email"
              className="field !w-64 !py-2 !pl-9 !text-xs"
              data-testid="admin-email-search"
              aria-label="Look up all enquiries from an email address (privacy requests)"
            />
          </div>
        </div>

        <p className="mt-4 text-xs text-zinc-600">
          Privacy: use the email lookup to fulfill access/deletion requests. Closed enquiries are auto-deleted after 90 days.
        </p>

        <div className="mt-8 space-y-px" data-testid="admin-enquiry-list">
          {loading && <p className="py-12 text-sm text-[#A8B4C2]" data-testid="admin-loading">Loading…</p>}
          {!loading && items.length === 0 && (
            <p className="glass p-12 text-sm text-[#A8B4C2]" data-testid="admin-empty">No enquiries match these filters yet.</p>
          )}
          {items.map((item) => (
            <article key={item.id} className="border border-white/10 bg-panel p-6" data-testid={`enquiry-${item.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="font-display text-lg font-bold text-white">{item.name}</h2>
                    <span className={`border px-2 py-0.5 font-display text-[10px] font-bold uppercase tracking-[0.2em] ${statusStyle[item.status]}`} data-testid={`enquiry-status-${item.id}`}>
                      {item.status}
                    </span>
                    <span className="border border-white/15 px-2 py-0.5 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-[#A8B4C2]">
                      {item.type === "booking" ? "Booking" : item.type === "health-check" ? "Health check" : "Message"}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-[#C9D2DE]">
                    {item.company ? `${item.company} · ` : ""}
                    <a href={`mailto:${item.email}`} className="text-brand hover:underline">{item.email}</a>
                    {item.phone ? ` · ${item.phone}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-zinc-600">{new Date(item.created_at).toLocaleString("en-US")}</span>
                  <select
                    value={item.status}
                    onChange={(e) => setStatus(item.id, e.target.value)}
                    className="field !w-auto !py-1.5 !text-xs"
                    data-testid={`enquiry-status-select-${item.id}`}
                    aria-label={`Set status for enquiry from ${item.name}`}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s} className="bg-panel">{s}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => deleteEnquiry(item.id, item.name)}
                    className="rounded-full border border-red-500/30 p-2 text-red-400 transition-colors duration-200 hover:bg-red-500/10"
                    data-testid={`enquiry-delete-${item.id}`}
                    aria-label={`Permanently delete enquiry from ${item.name}`}
                    title="Permanently delete (privacy request)"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {item.type === "booking" && (
                <p className="mt-4 border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-zinc-300" data-testid={`enquiry-slot-${item.id}`}>
                  Requested: <span className="text-brand">{item.preferred_date}</span> · {item.preferred_time} · Topic: {item.topic}
                </p>
              )}
              {item.message && <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-[#C9D2DE]">{item.message}</p>}
              {item.attachment && (
                <p className="mt-4 border border-white/10 bg-white/5 px-4 py-3 text-sm text-[#C9D2DE]" data-testid={`enquiry-attachment-${item.id}`}>
                  Attachment:{" "}
                  <a href={`${API}/admin/files/${item.attachment.id}`} className="text-brand hover:underline" data-testid={`enquiry-attachment-link-${item.id}`}>
                    {item.attachment.filename}
                  </a>{" "}
                  ({Math.max(1, Math.round(item.attachment.size / 1024))} KB)
                </p>
              )}
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}
